import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import Swal from 'sweetalert2';

// ── Types ────────────────────────────────────────────────────────────────────

interface ModuleRequest {
  id: string;
  tenant_id: string;
  module_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  message: string | null;
  price_monthly: number | null;
  created_at: string;
  updated_at: string;
  reviewed_at: string | null;
  // Joined
  tenant_name?: string;
  tenant_plan?: string;
  module_name?: string;
  module_category?: string;
  module_price?: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SW = { background: '#ffffff', color: '#0f172a' };

const STATUS_BADGE: Record<string, { bg: string; text: string; icon: string; label: string }> = {
  pending:   { bg: 'bg-amber-50',   text: 'text-amber-700',   icon: 'ri-time-line',            label: 'Pending' },
  approved:  { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-checkbox-circle-line',  label: 'Approved' },
  rejected:  { bg: 'bg-red-50',     text: 'text-red-600',     icon: 'ri-close-circle-line',     label: 'Rejected' },
  cancelled: { bg: 'bg-slate-100',  text: 'text-slate-500',   icon: 'ri-forbid-line',           label: 'Cancelled' },
};

const PLAN_BADGE: Record<string, string> = {
  free:       'bg-slate-100 text-slate-500',
  pro:        'bg-amber-50 text-amber-700',
  enterprise: 'bg-emerald-50 text-emerald-700',
};

const CAT_COLORS: Record<string, string> = {
  Production: 'text-emerald-600 bg-emerald-50',
  Website:    'text-cyan-600 bg-cyan-50',
  Automation: 'text-amber-600 bg-amber-50',
  Settings:   'text-rose-600 bg-rose-50',
};

const STATUS_TABS = ['all', 'pending', 'approved', 'rejected', 'cancelled'] as const;

// ── Component ─────────────────────────────────────────────────────────────────

export default function SAModuleRequestsPage() {
  const navigate = useNavigate();
  const [loading, setLoading]       = useState(true);
  const [requests, setRequests]     = useState<ModuleRequest[]>([]);
  const [activeTab, setActiveTab]   = useState<'all' | 'pending' | 'approved' | 'rejected' | 'cancelled'>('pending');
  const [processing, setProcessing] = useState<string | null>(null);

  // ── Load data ──────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    // Fetch requests + tenants + modules in parallel
    const [reqRes, tenantsRes, modulesRes] = await Promise.all([
      supabase
        .from('tenant_module_requests')
        .select('*')
        .order('created_at', { ascending: false }),
      supabase
        .from('saas_tenants')
        .select('id, name, plan_code'),
      supabase
        .from('saas_modules')
        .select('id, name, category, price_monthly'),
    ]);

    if (reqRes.error) {
      Swal.fire({ icon: 'error', title: 'Error loading requests', text: reqRes.error.message, ...SW });
      return;
    }

    const tenantMap = new Map<string, { name: string; plan_code: string }>(
      ((tenantsRes.data ?? []) as { id: string; name: string; plan_code: string }[]).map((t) => [t.id, t])
    );
    const moduleMap = new Map<string, { name: string; category: string; price_monthly: number }>(
      ((modulesRes.data ?? []) as { id: string; name: string; category: string; price_monthly: number }[]).map((m) => [m.id, m])
    );

    const enriched: ModuleRequest[] = ((reqRes.data ?? []) as ModuleRequest[]).map((r) => {
      const t = tenantMap.get(r.tenant_id);
      const m = moduleMap.get(r.module_id);
      return {
        ...r,
        tenant_name:      t?.name ?? r.tenant_id.slice(0, 8),
        tenant_plan:      t?.plan_code ?? '—',
        module_name:      m?.name ?? r.module_id,
        module_category:  m?.category ?? '—',
        module_price:     m?.price_monthly ?? 0,
      };
    });

    setRequests(enriched);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await load();
    };
    init();
  }, [navigate, load]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const approve = async (req: ModuleRequest) => {
    const { value: priceVal, isConfirmed } = await Swal.fire({
      title: `Approve "${req.module_name}"`,
      html: `
        <p style="color:#64748b;font-size:13px;margin-bottom:14px;text-align:left">
          Tenant: <strong style="color:#0f172a">${req.tenant_name}</strong> · Plan: <strong>${req.tenant_plan}</strong>
        </p>
        <p style="color:#64748b;font-size:12px;margin-bottom:6px;text-align:left">Monthly price charged to tenant (USD)</p>
      `,
      input: 'number',
      inputValue: req.price_monthly ?? req.module_price ?? 0,
      inputAttributes: { min: '0', step: '1', placeholder: '0' },
      showCancelButton: true,
      confirmButtonText: 'Approve & Activate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#94a3b8',
      ...SW,
    });

    if (!isConfirmed) return;
    setProcessing(req.id);

    const price = Number(priceVal) || 0;
    const now   = new Date().toISOString();

    // 1. Upsert tenant_modules — activates the module
    const { error: tmErr } = await supabase
      .from('tenant_modules')
      .upsert({
        tenant_id:     req.tenant_id,
        module_id:     req.module_id,
        is_enabled:    true,
        source:        'addon',
        price_monthly: price,
        starts_at:     now,
        updated_at:    now,
      }, { onConflict: 'tenant_id,module_id' });

    if (tmErr) {
      Swal.fire({ icon: 'error', title: 'Error activating module', text: tmErr.message, ...SW });
      setProcessing(null);
      return;
    }

    // 2. Update request status
    const { error: rqErr } = await supabase
      .from('tenant_module_requests')
      .update({ status: 'approved', reviewed_at: now, price_monthly: price, updated_at: now })
      .eq('id', req.id);

    if (rqErr) {
      Swal.fire({ icon: 'warning', title: 'Module activated but status update failed', text: rqErr.message, ...SW });
    } else {
      setRequests((prev) => prev.map((r) =>
        r.id === req.id ? { ...r, status: 'approved', reviewed_at: now, price_monthly: price } : r
      ));
      Swal.fire({
        icon: 'success',
        title: 'Module activated!',
        html: `<p style="color:#64748b;font-size:13px"><strong>${req.module_name}</strong> is now active for <strong>${req.tenant_name}</strong>.</p>`,
        timer: 2000,
        showConfirmButton: false,
        ...SW,
      });
    }
    setProcessing(null);
  };

  const reject = async (req: ModuleRequest) => {
    const { value: note, isConfirmed } = await Swal.fire({
      title: `Reject "${req.module_name}"`,
      html: `<p style="color:#64748b;font-size:13px;margin-bottom:10px;text-align:left">Tenant: <strong>${req.tenant_name}</strong></p>`,
      input: 'textarea',
      inputLabel: 'Rejection note (optional, visible to tenant)',
      inputPlaceholder: 'Reason for rejection...',
      inputAttributes: { maxlength: '300', style: 'height:80px;font-size:13px' },
      showCancelButton: true,
      confirmButtonText: 'Reject Request',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      ...SW,
    });

    if (!isConfirmed) return;
    setProcessing(req.id);

    const now = new Date().toISOString();
    const { error } = await supabase
      .from('tenant_module_requests')
      .update({ status: 'rejected', reviewed_at: now, message: note || req.message, updated_at: now })
      .eq('id', req.id);

    if (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW });
    } else {
      setRequests((prev) => prev.map((r) =>
        r.id === req.id ? { ...r, status: 'rejected', reviewed_at: now } : r
      ));
    }
    setProcessing(null);
  };

  const deactivate = async (req: ModuleRequest) => {
    const result = await Swal.fire({
      title: 'Deactivate module?',
      html: `<p style="color:#64748b;font-size:13px">Remove <strong>${req.module_name}</strong> access from <strong>${req.tenant_name}</strong>?</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      ...SW,
    });
    if (!result.isConfirmed) return;
    setProcessing(req.id);

    const { error } = await supabase
      .from('tenant_modules')
      .update({ is_enabled: false, updated_at: new Date().toISOString() })
      .eq('tenant_id', req.tenant_id)
      .eq('module_id', req.module_id);

    if (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message, ...SW });
    } else {
      // Optionally also revert the request status
      await supabase
        .from('tenant_module_requests')
        .update({ status: 'rejected', updated_at: new Date().toISOString() })
        .eq('id', req.id);
      setRequests((prev) => prev.map((r) =>
        r.id === req.id ? { ...r, status: 'rejected' } : r
      ));
      Swal.fire({ icon: 'success', title: 'Module deactivated', timer: 1500, showConfirmButton: false, ...SW });
    }
    setProcessing(null);
  };

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = activeTab === 'all' ? requests : requests.filter((r) => r.status === activeTab);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const stats = {
    total:    requests.length,
    pending:  requests.filter((r) => r.status === 'pending').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    rejected: requests.filter((r) => r.status === 'rejected').length,
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) return (
    <SuperAdminLayout>
      <SAHeader title="Module Requests" subtitle="Tenant activation requests" />
      <LoadingState />
    </SuperAdminLayout>
  );

  return (
    <SuperAdminLayout>
      <SAHeader
        title="Module Requests"
        subtitle="Review and approve tenant module activation requests"
        actions={
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line"></i></span>
            Refresh
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7 space-y-5">

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total',    value: stats.total,    icon: 'ri-inbox-line',           color: 'text-slate-900',   bg: 'bg-slate-100' },
            { label: 'Pending',  value: stats.pending,  icon: 'ri-time-line',            color: 'text-amber-600',   bg: 'bg-amber-50' },
            { label: 'Approved', value: stats.approved, icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Rejected', value: stats.rejected, icon: 'ri-close-circle-line',    color: 'text-red-500',     bg: 'bg-red-50' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <span className={`w-9 h-9 flex items-center justify-center rounded-lg ${s.bg} ${s.color} shrink-0`}>
                <i className={`${s.icon} text-base`}></i>
              </span>
              <div>
                <p className={`text-2xl font-bold leading-none ${s.color}`}>{s.value}</p>
                <p className="text-slate-400 text-xs mt-1">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Attention banner for pending */}
        {pendingCount > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
              <i className="ri-alarm-warning-line text-amber-600 text-sm"></i>
            </span>
            <p className="text-amber-700 text-sm">
              <strong>{pendingCount} request{pendingCount > 1 ? 's' : ''}</strong> waiting for review.
              <span className="text-amber-500 ml-1">Approve or reject to unblock tenant access.</span>
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1.5 overflow-x-auto">
          {STATUS_TABS.map((tab) => {
            const count = tab === 'all' ? requests.length : requests.filter((r) => r.status === tab).length;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap capitalize ${
                  activeTab === tab ? 'bg-[#0f172a] text-white' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                {tab}
                {count > 0 && (
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-full ${
                    activeTab === tab ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Requests list */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-slate-100 rounded-2xl mx-auto mb-3">
              <i className="ri-inbox-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-semibold text-sm mb-1">No {activeTab === 'all' ? '' : activeTab} requests</p>
            <p className="text-slate-400 text-xs">
              {activeTab === 'pending' ? 'All caught up!' : 'Nothing to show for this filter.'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[1fr_1fr_90px_80px_120px_160px] border-b border-slate-200 bg-slate-50">
              {['Tenant', 'Module', 'Plan', 'Price', 'Status', 'Actions'].map((h, i) => (
                <div key={h} className={`px-4 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider ${i === 5 ? 'text-right' : ''}`}>
                  {h}
                </div>
              ))}
            </div>

            {/* Rows */}
            {filtered.map((req, idx) => {
              const sb  = STATUS_BADGE[req.status] ?? STATUS_BADGE.pending;
              const isLast = idx === filtered.length - 1;
              const busy = processing === req.id;
              const catCls = CAT_COLORS[req.module_category ?? ''] ?? 'text-slate-500 bg-slate-50';

              return (
                <div
                  key={req.id}
                  className={`grid grid-cols-[1fr_1fr_90px_80px_120px_160px] items-start ${!isLast ? 'border-b border-slate-100' : ''} hover:bg-slate-50/60 transition-colors`}
                >
                  {/* Tenant */}
                  <div className="px-4 py-4">
                    <p className="text-slate-900 text-sm font-semibold">{req.tenant_name}</p>
                    <p className="text-slate-400 text-[11px] font-mono mt-0.5">{req.tenant_id.slice(0, 8)}…</p>
                    <p className="text-slate-400 text-[10px] mt-1">
                      {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {' · '}
                      {new Date(req.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>

                  {/* Module */}
                  <div className="px-4 py-4">
                    <p className="text-slate-800 text-sm font-medium">{req.module_name}</p>
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold mt-1 ${catCls}`}>
                      {req.module_category}
                    </span>
                    {req.message && (
                      <p className="text-slate-400 text-[11px] mt-1.5 leading-relaxed max-w-[200px] italic">
                        &ldquo;{req.message}&rdquo;
                      </p>
                    )}
                  </div>

                  {/* Plan */}
                  <div className="px-4 py-4">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase ${PLAN_BADGE[req.tenant_plan ?? ''] ?? 'bg-slate-100 text-slate-500'}`}>
                      {req.tenant_plan}
                    </span>
                  </div>

                  {/* Price */}
                  <div className="px-4 py-4">
                    <p className="text-slate-900 text-sm font-semibold">
                      {req.price_monthly != null ? `$${req.price_monthly}` : req.module_price ? `$${req.module_price}` : '—'}
                      {(req.price_monthly || req.module_price) ? <span className="text-slate-400 text-[10px] ml-0.5">/mo</span> : null}
                    </p>
                  </div>

                  {/* Status */}
                  <div className="px-4 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${sb.bg} ${sb.text}`}>
                      <i className={`${sb.icon} text-[11px]`}></i>
                      {sb.label}
                    </span>
                    {req.reviewed_at && (
                      <p className="text-slate-400 text-[10px] mt-1">
                        {new Date(req.reviewed_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="px-4 py-4 flex items-center justify-end gap-2">
                    {busy ? (
                      <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    ) : (
                      <>
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => approve(req)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-check-line"></i>
                              Approve
                            </button>
                            <button
                              onClick={() => reject(req)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-red-200"
                            >
                              <i className="ri-close-line"></i>
                              Reject
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && (
                          <button
                            onClick={() => deactivate(req)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-red-50 text-slate-500 hover:text-red-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-slate-200 hover:border-red-200"
                          >
                            <i className="ri-toggle-line"></i>
                            Revoke
                          </button>
                        )}
                        {req.status === 'rejected' && (
                          <button
                            onClick={() => approve(req)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-emerald-50 text-slate-500 hover:text-emerald-600 text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap border border-slate-200 hover:border-emerald-200"
                          >
                            <i className="ri-refresh-line"></i>
                            Approve now
                          </button>
                        )}
                        {req.status === 'cancelled' && (
                          <span className="text-slate-300 text-xs">Withdrawn</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="px-4 py-3 border-t border-slate-100">
              <p className="text-slate-400 text-xs">{filtered.length} request{filtered.length !== 1 ? 's' : ''} shown</p>
            </div>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}